import React, { useReducer, useCallback, useEffect, useRef } from 'react';
import { trackTeaserView, trackTeaserCtaClick } from '../../utils/formAiTeaserAnalytics';
import { safeNum, formatInt } from '../../utils/gpsFormUtils';

// Static, feature-flagged teaser for the deferred AI text-analysis feature.
// NO backend, NO AI call, NO real export. It measures appetite via a single
// analytics event, then degrades to a calm confirmation micro-state. The blurred
// "Aperçu" preview is illustrative only and stays non-interactive in every state.

const PITCH =
  'Bientôt : une synthèse automatique de vos commentaires terrain — thèmes récurrents, sentiment, verbatims clés — sans dépouiller vos réponses libres une par une.';
const HONESTY =
  'Fonctionnalité en cours de conception — aucune analyse n\'est générée pour le moment.';
const HELPER =
  'Dites-nous si cette analyse vous serait utile — ça nous aide à prioriser.';
const CONFIRMATION =
  'Merci ! On vous préviendra dès que l\'analyse IA sera disponible.';

const PREVIEW_THEMES = ['Ruptures de stock', 'PLV abîmée', 'Personnel accueillant', 'Délais respectés'];
const PREVIEW_VERBATIM = '« Vitrine déjà installée, rien à signaler sur cette visite. »';

const Badge = () => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
    Bientôt
  </span>
);

// Frosted/blurred, dimmed, non-interactive preview of the future render. Hidden
// from assistive tech; every control inside is visibly disabled.
const BlurredPreview = () => (
  <div className="relative mt-3 select-none" aria-hidden="true" data-testid="form-ai-teaser-preview">
    <span className="absolute top-2 right-2 z-10 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-gray-200/80 dark:bg-gray-700/80 text-gray-500 dark:text-gray-300">
      Aperçu
    </span>
    <div className="pointer-events-none opacity-50 blur-[2px] max-h-[140px] overflow-hidden">
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-2.5">
        <div className="space-y-1.5">
          <div className="h-2.5 rounded bg-gray-200 dark:bg-gray-700 w-full" />
          <div className="h-2.5 rounded bg-gray-200 dark:bg-gray-700 w-3/5" />
        </div>
        <div className="flex flex-wrap gap-2">
          {PREVIEW_THEMES.map((t) => (
            <span key={t} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
              {t}
            </span>
          ))}
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded">
          <div className="bg-emerald-400" style={{ width: '58%' }} />
          <div className="bg-gray-300 dark:bg-gray-600" style={{ width: '27%' }} />
          <div className="bg-red-400" style={{ width: '15%' }} />
        </div>
        <p className="text-xs italic text-gray-500 dark:text-gray-400 truncate">{PREVIEW_VERBATIM}</p>
      </div>
    </div>
    {/* Soft gradient/overlay reinforcing the "not real data" read. */}
    <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-white/60 dark:from-transparent dark:to-[#1e1e1e]/60 rounded-lg" />
  </div>
);

const initialState = { acknowledged: false };
const reducer = (state, action) =>
  action.type === 'ACK' ? { ...state, acknowledged: true } : state;

const GPSFormAITeaser = ({ respondents, project, currentUser }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const rootRef = useRef(null);

  // One analytics event per click (double-click → two events); the ACK dispatch
  // runs even if tracking throws (the wrapper swallows its own errors, and the
  // dispatch is outside it regardless).
  const handleCta = useCallback(
    (cta) => {
      trackTeaserCtaClick({ project, currentUser, cta, respondents });
      dispatch({ type: 'ACK' });
    },
    [project, currentUser, respondents],
  );

  const handleNotify = useCallback(() => handleCta('notify'), [handleCta]);
  const handleInterested = useCallback(() => handleCta('interested'), [handleCta]);

  // Impression event: fire once when ≥50% of the teaser is visible. Keyed by
  // questionId via the parent's `key`, so a fresh mount per question resets it;
  // the ref guard prevents duplicate fires across observer callbacks.
  const viewedRef = useRef(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !viewedRef.current) {
            viewedRef.current = true;
            trackTeaserView({ project, currentUser, respondents });
          }
        });
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [project, currentUser, respondents]);

  const count = formatInt(safeNum(respondents));

  return (
    <div
      ref={rootRef}
      id="form-ai-teaser"
      className="bg-white dark:bg-[#1e1e1e] p-4 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm"
      data-testid="form-ai-teaser"
    >
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Analyse IA</h3>
        <Badge />
      </div>

      <div className="flex items-start gap-2">
        <span className="text-violet-500 text-lg leading-none" aria-hidden="true">✨</span>
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          L'IA lira vos réponses libres à votre place.
        </p>
      </div>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 leading-snug">
        {PITCH} <span className="text-gray-500 dark:text-gray-400" aria-hidden="true">· </span>
        <span className="text-gray-500 dark:text-gray-400">{count} réponses</span>
      </p>

      <BlurredPreview />

      {state.acknowledged ? (
        <div
          className="mt-3 flex items-center gap-2 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 px-4 py-2.5"
          role="status"
          data-testid="form-ai-teaser-confirmation"
        >
          <span className="text-violet-500" aria-hidden="true">✓</span>
          <p className="text-sm font-medium text-violet-700 dark:text-violet-300">{CONFIRMATION}</p>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleNotify}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white transition-colors"
          >
            <span aria-hidden="true">✨</span> Être prévenu·e
          </button>
          <button
            type="button"
            onClick={handleInterested}
            className="inline-flex items-center px-3 h-9 rounded-lg text-sm font-medium text-violet-700 dark:text-violet-300 hover:underline"
          >
            Ça m'intéresse
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">{HELPER}</span>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">{HONESTY}</p>
    </div>
  );
};

export default GPSFormAITeaser;
